import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FILE_BASE_URL } from '../../../api/axios';
import tetfundLogo from '../../../assets/images/TetfundLogo.svg';
import {
    reportBatchService,
    userService,
    ApprovalRequestItem,
    ApprovalStatus,
    ApprovalType,
    APPROVAL_STATUS_LABELS,
    APPROVAL_TYPE_LABELS,
} from '../services/adminAuth';

// Badge colors per approval status: [text RGB, light background RGB].
const STATUS_COLORS: Record<number, { text: [number, number, number]; bg: [number, number, number] }> = {
    [ApprovalStatus.Approved]: { text: [21, 128, 61], bg: [220, 252, 231] },
    [ApprovalStatus.Rejected]: { text: [185, 28, 28], bg: [254, 226, 226] },
    [ApprovalStatus.Pending]: { text: [161, 98, 7], bg: [254, 243, 199] },
};

type Notify = (
    status: 'success' | 'error' | 'warning' | 'info',
    options: { title: string; message: string; timer?: number },
) => void;

// ── Helpers ──────────────────────────────────────────────────────────────────

const authorsText = (authors: { name: string; isLeadAuthor: boolean }[] | null | undefined) => {
    if (!authors || authors.length === 0) return '—';
    const ordered = [...authors].sort((a, b) => (b.isLeadAuthor ? 1 : 0) - (a.isLeadAuthor ? 1 : 0));
    return ordered.map(a => a.name).filter(Boolean).join(', ') || '—';
};

const namesText = (arr: { name: string }[] | null | undefined) =>
    (arr && arr.length > 0) ? arr.map(x => x.name).filter(Boolean).join(', ') : '—';

const fmtDate = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

const sanitize = (s: string) => (s || 'report').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase();

// Spells out a whole number (e.g. 3 → "three") for the approval note.
const numberToWords = (n: number): string => {
    if (!Number.isFinite(n) || n < 0) return String(n);
    const ones = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
        'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? '-' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' and ' + numberToWords(n % 100) : '');
    return String(n);
};

interface LoadedImage { dataUrl: string; format: 'JPEG' | 'PNG'; w: number; h: number; }

const loadImage = async (url: string): Promise<LoadedImage | null> => {
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const blob = await res.blob();
        const format: 'JPEG' | 'PNG' = blob.type.includes('png') ? 'PNG' : 'JPEG';
        const dataUrl: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
        const dims = await new Promise<{ w: number; h: number }>((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
            img.onerror = () => resolve({ w: 0, h: 0 });
            img.src = dataUrl;
        });
        return { dataUrl, format, w: dims.w, h: dims.h };
    } catch {
        return null;
    }
};

// Loads a remote image for embedding: tries an <img> + canvas first (works when the host
// allows cross-origin canvas reads), then falls back to a fetch → blob approach.
const loadRemoteImage = async (url: string): Promise<LoadedImage | null> => {
    // Cache-buster: forces a fresh (CORS-enabled) fetch rather than reusing a
    // previously cached non-CORS copy of the same image (e.g. from an <img> tag).
    const bust = url + (url.includes('?') ? '&' : '?') + '_cb=' + Date.now();
    const format: 'JPEG' | 'PNG' = /\.png(\?|$)/i.test(url) ? 'PNG' : 'JPEG';

    const viaCanvas = await new Promise<LoadedImage | null>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                if (!img.naturalWidth) return resolve(null);
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) return resolve(null);
                ctx.drawImage(img, 0, 0);
                resolve({
                    dataUrl: canvas.toDataURL(format === 'PNG' ? 'image/png' : 'image/jpeg', 0.95),
                    format,
                    w: canvas.width,
                    h: canvas.height,
                });
            } catch {
                resolve(null); // tainted canvas → host didn't allow cross-origin read
            }
        };
        img.onerror = () => resolve(null);
        img.src = bust;
    });
    return viaCanvas ?? loadImage(bust);
};

// Rasterises the (SVG) logo to a PNG for the page header. Same-origin asset — no CORS.
const loadLogo = async (url: string, targetW = 700): Promise<LoadedImage | null> => {
    try {
        const img = new Image();
        const ok = await new Promise<boolean>((resolve) => {
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
        });
        if (!ok) return null;
        const natW = img.naturalWidth || targetW;
        const natH = img.naturalHeight || Math.round(targetW * 0.32);
        const scale = targetW / natW;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(natW * scale);
        canvas.height = Math.round(natH * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        return { dataUrl: canvas.toDataURL('image/png'), format: 'PNG', w: canvas.width, h: canvas.height };
    } catch {
        return null;
    }
};

// Loads the watermark image and knocks out its (near-white) background to transparent,
// so only the faint logo tints the page rather than a white box washing out the text.
const loadWatermark = async (url: string): Promise<LoadedImage | null> => {
    try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        const ok = await new Promise<boolean>((resolve) => {
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
        });
        if (!ok || !img.naturalWidth) return null;
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0);
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = imgData.data;
        for (let i = 0; i < d.length; i += 4) {
            if (d[i] >= 245 && d[i + 1] >= 245 && d[i + 2] >= 245) d[i + 3] = 0;
        }
        ctx.putImageData(imgData, 0, 0);
        return { dataUrl: canvas.toDataURL('image/png'), format: 'PNG', w: canvas.width, h: canvas.height };
    } catch {
        return null;
    }
};

// ── Export ───────────────────────────────────────────────────────────────────

/**
 * Fetches the report's records + the current user's signature and produces a
 * paginated PDF with a header, footer, meta block, records table and signature.
 */
export const exportApprovalReportPdf = async (request: ApprovalRequestItem, notify: Notify): Promise<void> => {
    notify('info', { title: 'Preparing report', message: 'Generating the PDF, please wait…' });

    try {
        const [itemsRes, sigRes, watermark, logo] = await Promise.all([
            reportBatchService.getReportBatchItems(request.reportBatchId),
            userService.getSignature().catch(() => null),
            loadWatermark(`${process.env.PUBLIC_URL || ''}/watermark.jpeg`).catch(() => null),
            loadLogo(tetfundLogo).catch(() => null),
        ]);

        if (!itemsRes.isSuccess) {
            notify('error', { title: 'Export failed', message: itemsRes.message || 'Could not load the report records.' });
            return;
        }

        const details = itemsRes.data.approvalRequestDetail ?? [];

        // GetSignature may return either a base64 data URL (no CORS needed) or a file path.
        const sigData = sigRes && sigRes.isSuccess ? sigRes.data : null;
        let signature: LoadedImage | null = null;
        if (sigData) {
            if (sigData.startsWith('data:')) {
                signature = await loadImage(sigData);
            } else {
                const sigUrl = sigData.startsWith('http') ? sigData : `${FILE_BASE_URL}/${sigData.replace(/^\//, '')}`;
                signature = await loadRemoteImage(sigUrl);
            }
        }

        // Header title: "Report Type (Period)".
        const typeLabel = APPROVAL_TYPE_LABELS[request.reportType] || 'Report';
        const reportTitle = request.periodLabel ? `${typeLabel} (${request.periodLabel})` : typeLabel;
        const statusLabel = APPROVAL_STATUS_LABELS[request.status] || 'Unknown';
        const generatedOn = new Date().toLocaleString();

        const doc = new jsPDF('landscape');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 14;

        // Header drawn on every page: centered logo, then the org text lines.
        const drawHeader = () => {
            let textTop = 13;
            if (logo && logo.w && logo.h) {
                const logoH = 13;
                const logoW = logoH * (logo.w / logo.h);
                doc.addImage(logo.dataUrl, 'PNG', (pageWidth - logoW) / 2, 6, logoW, logoH);
                textTop = 24; // push the text below the logo
            }
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.setTextColor(21, 128, 61);
            doc.text('TERTIARY EDUCATION TRUST FUND (TETFUND)', pageWidth / 2, textTop, { align: 'center' });
            // "Technical Advisory Group (TAG)" line intentionally hidden (commented out per request).
            // doc.setFontSize(11);
            // doc.setTextColor(60);
            // doc.text('Technical Advisory Group (TAG)', pageWidth / 2, textTop + 6, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(90);
            doc.text(reportTitle, pageWidth / 2, textTop + 6, { align: 'center' });
            doc.setDrawColor(210);
            doc.line(marginX, textTop + 9, pageWidth - marginX, textTop + 9);
            doc.setTextColor(0);
        };

        // With the logo, the header runs to ~y=33; without it, ~y=22.
        const headerBottom = (logo && logo.w && logo.h) ? 33 : 22;

        // ── Meta block (first page only) ──────────────────────────────────────
        drawHeader();
        let y = headerBottom + 7;
        doc.setFontSize(9);
        const metaLine = (label: string, value: string) => {
            if (!value) return;
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(70);
            doc.text(`${label}:`, marginX, y);
            const labelWidth = doc.getTextWidth(`${label}: `);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0);
            doc.text(value, marginX + labelWidth + 1, y);
            y += 5.5;
        };
        metaLine('Report Type', APPROVAL_TYPE_LABELS[request.reportType] || '—');
        metaLine('Period', request.periodLabel || '');
        metaLine('Requested by', request.nameOfRequestedUser || '');
        metaLine('Total records', String(details.length));
        if (request.remark) metaLine('Remark', request.remark);

        // Status badge (colored) on the right, with "Approved/Rejected by …" beneath it.
        const colors = STATUS_COLORS[request.status] || { text: [71, 85, 105] as [number, number, number], bg: [241, 245, 249] as [number, number, number] };
        const badgeText = statusLabel.toUpperCase();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(17);
        const padX = 6;
        const badgeW = doc.getTextWidth(badgeText) + padX * 2;
        const badgeH = 12;
        const badgeX = pageWidth - marginX - badgeW;
        const badgeY = headerBottom + 5;
        doc.setFillColor(colors.bg[0], colors.bg[1], colors.bg[2]);
        doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 2, 2, 'F');
        doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
        doc.text(badgeText, badgeX + padX, badgeY + 8.4);

        if (request.nameOfApprovingUser) {
            const dateStr = fmtDate(request.approvedAt);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(70);
            doc.text(`${statusLabel} by: ${request.nameOfApprovingUser}`, pageWidth - marginX, badgeY + badgeH + 5, { align: 'right' });
            if (dateStr) doc.text(dateStr, pageWidth - marginX, badgeY + badgeH + 9.5, { align: 'right' });
        }
        doc.setTextColor(0);

        // ── Records table ─────────────────────────────────────────────────────
        // For reviewer payments, list each reviewer's account number under their name.
        const isReviewerPayment = request.reportType === ApprovalType.ReviewerPayments;
        const reviewersCell = (d: typeof details[number]) => {
            if (isReviewerPayment && d.reviewerDetails && d.reviewerDetails.length > 0) {
                return d.reviewerDetails
                    .map(r => r.accountNumber ? `${r.name || '—'}\nAcct: ${r.accountNumber}` : (r.name || '—'))
                    .join('\n');
            }
            return namesText(d.reviewerDetails);
        };

        const body = details.map((d, i) => [
            i + 1,
            d.submissionNumber || '—',
            d.title || '—',
            authorsText(d.authorDetails),
            reviewersCell(d),
            d.remarks || '—',
        ]);

        autoTable(doc, {
            head: [['S/N', 'Sub. No', 'Title', 'Authors', 'Reviewers', 'Remarks']],
            body,
            startY: y + 3,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak', valign: 'top' },
            headStyles: { fillColor: [21, 128, 61], textColor: 255, fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 12, halign: 'center' },
                1: { cellWidth: 24 },
                3: { cellWidth: 55 },
                4: { cellWidth: 50 },
                5: { cellWidth: 45 },
            },
            margin: { top: headerBottom + 4, bottom: 16, left: marginX, right: marginX },
            didDrawPage: (hook) => {
                // Header on pages after the first (first page header already drawn above).
                if (hook.pageNumber > 1) drawHeader();
            },
        });

        // ── Approval note (Successful Submissions, once approved) ─────────────
        // States the ES's approval so the Procurement Unit can proceed.
        let contentBottom = (doc as any).lastAutoTable?.finalY ?? y;
        let sigGap = 14; // table → signature gap for reports without a note
        if (request.reportType === ApprovalType.SuccessfulSubmissions && request.status === ApprovalStatus.Approved) {
            const count = details.length;
            const noun = count === 1 ? 'manuscript' : 'manuscripts';
            const countText = `${count} (${numberToWords(count)})`;
            // Segments of the paragraph; the count is rendered bold inline.
            const segments: { text: string; bold: boolean }[] = [
                { text: 'The Executive Secretary has approved the conveyance of the', bold: false },
                { text: countText, bold: true },
                {
                    text: `${noun} listed above to the Procurement Unit to initiate the due process, and the engagement of reputable publishers for their publication. The Procurement Unit may therefore proceed accordingly.`,
                    bold: false,
                },
            ];
            const words: { word: string; bold: boolean }[] = [];
            segments.forEach(seg =>
                seg.text.split(/\s+/).filter(Boolean).forEach(w => words.push({ word: w, bold: seg.bold })),
            );

            doc.setFontSize(9.5);
            doc.setTextColor(30);
            const lineStep = 5;
            const maxWidth = pageWidth - marginX * 2;

            // Rough line-count estimate for the page-break check.
            doc.setFont('helvetica', 'normal');
            const estLines = doc.splitTextToSize(words.map(w => w.word).join(' '), maxWidth).length;
            let noteY = contentBottom + 10;
            if (noteY + estLines * lineStep + 4 > pageHeight - 16) {
                doc.addPage();
                drawHeader();
                noteY = headerBottom + 10;
            }

            // Word-by-word layout so the bold count sits inline with the flowing text.
            const spaceW = doc.getTextWidth(' ');
            let cx = marginX;
            let cy = noteY;
            words.forEach(({ word, bold }) => {
                doc.setFont('helvetica', bold ? 'bold' : 'normal');
                const ww = doc.getTextWidth(word);
                if (cx > marginX && cx + ww > marginX + maxWidth) {
                    cx = marginX;
                    cy += lineStep;
                }
                doc.text(word, cx, cy);
                cx += ww + spaceW;
            });
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0);
            contentBottom = cy;
            sigGap = 9; // tighter gap between the note and the signature
        }

        // ── Signature ─────────────────────────────────────────────────────────
        let sigY = contentBottom + sigGap;
        if (sigY + 50 > pageHeight - 16) {
            doc.addPage();
            drawHeader();
            sigY = headerBottom + 6;
        }

        if (signature) {
            const maxW = 55, maxH = 22;
            let w = maxW, h = maxH;
            if (signature.w && signature.h) {
                const ar = signature.w / signature.h;
                h = maxW / ar;
                if (h > maxH) { h = maxH; w = maxH * ar; } else { w = maxW; }
            }
            doc.addImage(signature.dataUrl, signature.format, marginX, sigY, w, h);
            sigY += h + 1;
        } else {
            sigY += 12;
        }

        doc.setDrawColor(120);
        doc.line(marginX, sigY, marginX + 70, sigY);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(0);
        doc.text('Signature', marginX, sigY + 5);
        doc.setFontSize(8);
        if (request.nameOfApprovingUser) {
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(40);
            doc.text(request.nameOfApprovingUser.trim(), marginX, sigY + 10);
        }
        // Position of the approver (always the ES; not returned in the payload).
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(90);
        doc.text('Executive Secretary (ES)', marginX, sigY + 14.5);
        const decisionDate = fmtDate(request.approvedAt);
        if (decisionDate) doc.text(`Date: ${decisionDate}`, marginX, sigY + 19);

        // Watermark + footer on every page.
        let wm: { w: number; h: number; x: number; y: number } | null = null;
        if (watermark && watermark.w && watermark.h) {
            const wmW = pageWidth * 0.42;
            const wmH = wmW * (watermark.h / watermark.w);
            wm = { w: wmW, h: wmH, x: (pageWidth - wmW) / 2, y: (pageHeight - wmH) / 2 };
        }

        const totalPages = (doc as any).getNumberOfPages();
        for (let p = 1; p <= totalPages; p++) {
            doc.setPage(p);

            if (watermark && wm) {
                const anyDoc = doc as any;
                anyDoc.saveGraphicsState();
                anyDoc.setGState(new anyDoc.GState({ opacity: 0.32 }));
                doc.addImage(watermark.dataUrl, 'PNG', wm.x, wm.y, wm.w, wm.h);
                anyDoc.restoreGraphicsState();
            }

            doc.setFontSize(8);
            doc.setTextColor(130);
            doc.setDrawColor(220);
            doc.line(marginX, pageHeight - 12, pageWidth - marginX, pageHeight - 12);
            doc.text(`Generated on ${generatedOn}`, marginX, pageHeight - 7);
            doc.text(`Page ${p} of ${totalPages}`, pageWidth - marginX, pageHeight - 7, { align: 'right' });
        }

        doc.save(`${sanitize(reportTitle)}_${sanitize(statusLabel)}_${new Date().toISOString().split('T')[0]}.pdf`);
        notify('success', { title: 'Report ready', message: 'Your report has been downloaded.', timer: 3000 });
    } catch (err) {
        notify('error', { title: 'Export failed', message: 'An error occurred while generating the report.' });
    }
};
